import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import './JDMAAvis.css';
import { immatCorrespond } from '../utils/immat';

const TITRES = {
  vn: 'JDMA — Avis Ventes VN',
  apv: 'JDMA — Avis Atelier APV',
};

// Colonnes affichées par défaut dans le tableau (extraites du JSONB data)
// Le reste reste dans le drawer détail.
const COLS_AFFICHAGE = [
  { key: 'Date',                label: 'Date',          type: 'date' },
  { key: 'Établissement',       label: 'Établissement', type: 'text' },
  { key: 'Marque',              label: 'Marque',        type: 'text' },
  { key: 'modele',              label: 'Modèle',        type: 'text' },
  { key: 'immatriculation',     label: 'Immat',         type: 'text' },
  { key: 'Nom Feedbacker',      label: 'Client',        type: 'text' },
  { key: 'Nom vendeur',         label: 'Vendeur',       type: 'text' },
  { key: 'Réponse #1',          label: 'Réponse Q1',    type: 'text' },
];

function JDMAAvis({ type, user, editMode = false, concessions = [] }) {
  const [entries, setEntries]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [toast, setToast]                     = useState(null);
  const [selected, setSelected]               = useState(new Set());
  const [drawerEntry, setDrawerEntry]         = useState(null);

  const isSuperAdmin = user?.role === 'super-admin';
  const showActions = editMode && isSuperAdmin;

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/jdma/${type}`);
      setEntries(res.data.entries || []);
    } catch (err) {
      console.error(`Erreur chargement JDMA ${type}:`, err);
      showToast('Erreur de chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [type]);

  const showToast = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDelete = async (id) => {
    if (!isSuperAdmin || !window.confirm('Supprimer cet avis ?')) return;
    try {
      await api.delete(`/jdma/${type}/${id}`);
      setEntries(prev => prev.filter(e => e.id !== id));
      showToast('Avis supprimé');
    } catch (err) {
      showToast('Erreur de suppression', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!isSuperAdmin || selected.size === 0) return;
    if (!window.confirm(`Supprimer ${selected.size} avis ?`)) return;
    try {
      await api.delete(`/jdma/${type}/bulk`, { data: { ids: Array.from(selected) } });
      setEntries(prev => prev.filter(e => !selected.has(e.id)));
      setSelected(new Set());
      showToast(`${selected.size} avis supprimés`);
    } catch (err) {
      showToast('Erreur de suppression', 'error');
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(e => e.id)));
  };

  // Filtrage
  const filtered = useMemo(() => {
    let list = entries;
    if (filterConcession) {
      list = list.filter(e => String(e.concession_id) === String(filterConcession));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => {
        const d = e.data || {};
        return (
          (d['Établissement'] || '').toLowerCase().includes(q) ||
          (d['Nom Feedbacker'] || '').toLowerCase().includes(q) ||
          (d['Prénom Feedbacker'] || '').toLowerCase().includes(q) ||
          immatCorrespond(d['immatriculation'], q) ||
          (d['Nom vendeur'] || '').toLowerCase().includes(q) ||
          (d['Marque'] || '').toLowerCase().includes(q)
        );
      });
    }
    return list;
  }, [entries, filterConcession, search]);

  // Liste des concessions présentes dans les données (pour le filtre)
  const concessionsActives = useMemo(() => {
    const ids = new Set();
    entries.forEach(e => { if (e.concession_id) ids.add(e.concession_id); });
    return concessions.filter(c => ids.has(c.id));
  }, [entries, concessions]);

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return d; }
  };

  return (
    <div className="jdma-container">
      <div className="jdma-header">
        <h2>{TITRES[type]}</h2>
      </div>

      <div className="jdma-toolbar">
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={`page-select ${filterConcession ? 'active-filter' : ''}`}
          value={filterConcession}
          onChange={(e) => setFilterConcession(e.target.value)}
        >
          <option value="">Toutes concessions</option>
          {concessionsActives.map(c => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>
        <span className="jdma-count">
          {loading ? 'Chargement...' : `${filtered.length} avis`}
        </span>
        {showActions && selected.size > 0 && (
          <button className="btn-danger" onClick={handleBulkDelete}>
            🗑️ Supprimer la sélection ({selected.size})
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="jdma-table">
          <thead>
            <tr>
              {showActions && (
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                  />
                </th>
              )}
              {COLS_AFFICHAGE.map(c => <th key={c.key}>{c.label}</th>)}
              <th>Concession CRM</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={COLS_AFFICHAGE.length + 3} className="jdma-empty">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={COLS_AFFICHAGE.length + 3} className="jdma-empty">
                {entries.length === 0
                  ? 'Aucun avis. La synchronisation automatique est gérée dans Administration → Bases de données → Sync automatique.'
                  : 'Aucun résultat avec ces filtres.'}
              </td></tr>
            )}
            {!loading && filtered.map(entry => {
              const d = entry.data || {};
              return (
                <tr key={entry.id} className={selected.has(entry.id) ? 'selected' : ''}>
                  {showActions && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(entry.id)}
                        onChange={() => toggleSelect(entry.id)}
                      />
                    </td>
                  )}
                  {COLS_AFFICHAGE.map(c => {
                    let val = d[c.key];
                    if (c.type === 'date') val = formatDate(val);
                    return (
                      <td key={c.key} className="jdma-cell">
                        {val || <span className="jdma-empty-val">—</span>}
                      </td>
                    );
                  })}
                  <td>
                    {entry.concession_nom
                      ? <span className="jdma-badge">{entry.concession_nom}</span>
                      : <span className="jdma-empty-val">—</span>
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Voir détail" onClick={() => setDrawerEntry(entry)}>👁️</button>
                      {showActions && (
                        <button className="btn-icon delete" title="Supprimer" onClick={() => handleDelete(entry.id)}>🗑️</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Drawer détail */}
      {drawerEntry && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Avis #{drawerEntry.id_reponse}</h2>
              <button className="modal-close" onClick={() => setDrawerEntry(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="jdma-meta">
                <div><strong>Date :</strong> {formatDate(drawerEntry.date_reponse)}</div>
                <div><strong>Enquête :</strong> {drawerEntry.id_enquete} — {drawerEntry.objet}</div>
                <div><strong>Établissement source :</strong> {drawerEntry.etablissement_source || '—'}</div>
                <div><strong>Concession CRM :</strong> {drawerEntry.concession_nom || '—'}</div>
              </div>
              <h3 className="jdma-section">Toutes les colonnes</h3>
              <table className="jdma-detail-table">
                <tbody>
                  {Object.entries(drawerEntry.data || {})
                    .filter(([k, v]) => v !== null && v !== '')
                    .map(([k, v]) => (
                      <tr key={k}>
                        <td className="jdma-detail-key">{k}</td>
                        <td className="jdma-detail-val">{String(v)}</td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDrawerEntry(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`jdma-toast ${toast.kind}`}>{toast.msg}</div>
      )}
    </div>
  );
}

export default JDMAAvis;
